import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";
import { ArrowDownWideNarrow } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { SearchField } from "@/features/search/SearchField";
import { cn } from "@/lib/utils";
import "./Inventory.css";
import { CAPTION, ROW_TITLE, SUBTITLE } from "@/lib/typography";

/** Catalog layout and controls only. Pages retain their queries, state and actions. */
export function InventoryPage({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLElement>) {
  return (
    <section className={cn("page-frame page-content", className)} {...props}>
      {children}
    </section>
  );
}

export function InventoryHeader({
  navigation,
  title,
  titleId,
  subtitle,
  search = <SearchField />,
  action,
  meta,
  children,
  titleClassName,
}: {
  navigation?: ReactNode;
  title: ReactNode;
  titleId?: string;
  subtitle?: ReactNode;
  search?: ReactNode;
  action?: ReactNode;
  /** Facts about the page, full width under the title and tools. */
  meta?: ReactNode;
  children?: ReactNode;
  titleClassName?: string;
}) {
  return (
    <header className="inventory-header">
      <div className={cn("inventory-heading", !search && !action && "inventory-heading--standalone")}>
        <div className="inventory-title">
          {navigation}
          <div className="min-w-0">
            <h1 id={titleId} className={cn("page-title", titleClassName)}>{title}</h1>
            {subtitle && <p className={cn(SUBTITLE, "inventory-subtitle")}>{subtitle}</p>}
          </div>
        </div>
        {(search || action) && (
          <div className="inventory-tools">
            {search && <div className="inventory-search-slot">{search}</div>}
            {action && <div className="action-row inventory-actions">{action}</div>}
          </div>
        )}
      </div>
      {meta}
      {children && <div className="inventory-controls">{children}</div>}
    </header>
  );
}

export function InventorySort<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Record<T, { label: string }>;
  onChange: (value: T) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-control"
          className="ml-auto"
          aria-label={`${label}: ${options[value].label}`}
          title={`${label}: ${options[value].label}`}
        >
          <ArrowDownWideNarrow aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup value={value} onValueChange={next => onChange(next as T)}>
          {(Object.keys(options) as T[]).map(key => (
            <DropdownMenuRadioItem key={key} value={key}>
              {options[key].label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function InventoryGrid({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("inventory-grid", className)} {...props} />;
}

export function InventoryRow({
  icon,
  title,
  description,
  className,
  ...props
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, "title"> & {
  icon: ReactNode;
  title: ReactNode;
  description: ReactNode;
}) {
  return (
    <button type="button" className={cn("inventory-row", className)} {...props}>
      <span className="inventory-icon">{icon}</span>
      <span className="inventory-copy">
        <strong className={ROW_TITLE}>{title}</strong>
        <small className={CAPTION}>{description}</small>
      </span>
    </button>
  );
}
